
import { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

export default function Overview({ supabase }) {
  const [stats, setStats] = useState({ users: 0, orders: 0, revenue: 0, providers: 0, products: 0, services: 0, bookings: 0, reviews: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStats(); }, []);

  async function loadStats() {
    const [users, orders, providers, products, services, bookings, reviews] = await Promise.all([
      supabase.from("profiles").select("id", { count: "exact" }),
      supabase.from("orders").select("id, total_amount, created_at", { count: "exact" }),
      supabase.from("shops").select("id", { count: "exact" }),
      supabase.from("products").select("id", { count: "exact" }),
      supabase.from("services").select("id", { count: "exact" }),
      supabase.from("bookings").select("id", { count: "exact" }),
      supabase.from("reviews").select("id", { count: "exact" }),
    ]);
    const totalRevenue = orders.data?.reduce((sum, o) => sum + (o.total_amount || 0), 0) || 0;
    setStats({ users: users.count || 0, orders: orders.count || 0, revenue: totalRevenue, providers: providers.count || 0, products: products.count || 0, services: services.count || 0, bookings: bookings.count || 0, reviews: reviews.count || 0 });
    const { data: recent } = await supabase.from("orders").select("*").order("created_at", { ascending: false }).limit(5);
    if (recent) setRecentOrders(recent);
    setLoading(false);
  }

  const statCards = [
    { label: "Total Users", value: stats.users, icon: "👥", color: "#0057A8" },
    { label: "Total Orders", value: stats.orders, icon: "📦", color: "#00A651" },
    { label: "Total Revenue", value: "Le " + stats.revenue.toLocaleString(), icon: "💰", color: "#f39c12" },
    { label: "Active Shops", value: stats.providers, icon: "🏪", color: "#9b59b6" },
    { label: "Products", value: stats.products, icon: "🛍️", color: "#27ae60" },
    { label: "Services", value: stats.services, icon: "✂️", color: "#e74c3c" },
    { label: "Bookings", value: stats.bookings, icon: "📅", color: "#2980b9" },
    { label: "Reviews", value: stats.reviews, icon: "⭐", color: "#f1c40f" },
  ];

  const chartData = [
    { name: "Mon", orders: 4, revenue: 85000 },
    { name: "Tue", orders: 7, revenue: 142000 },
    { name: "Wed", orders: 5, revenue: 98000 },
    { name: "Thu", orders: 9, revenue: 187000 },
    { name: "Fri", orders: 12, revenue: 243000 },
    { name: "Sat", orders: 15, revenue: 312000 },
    { name: "Sun", orders: 8, revenue: 165000 },
  ];

  const statusColor = { pending: "#f39c12", confirmed: "#0057A8", ready_for_pickup: "#9b59b6", in_transit: "#00A651", delivered: "#27ae60", cancelled: "#e74c3c" };

  if (loading) return <div style={{ color: "#666", textAlign: "center", padding: 40 }}>Loading dashboard...</div>;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        {statCards.map(card => (
          <div key={card.label} style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 20, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 12, background: card.color + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{card.icon}</div>
            <div>
              <div style={{ fontSize: 24, fontWeight: "bold", color: card.color }}>{card.value}</div>
              <div style={{ color: "#666", fontSize: 13, marginTop: 2 }}>{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 20 }}>
          <h3 style={{ color: "#fff", fontSize: 15, fontWeight: "600", margin: "0 0 16px 0" }}>Orders This Week</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="name" stroke="#666" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis stroke="#666" tick={{ fill: "#666", fontSize: 12 }} />
              <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #333", color: "#fff" }} />
              <Line type="monotone" dataKey="orders" stroke="#00A651" strokeWidth={2} dot={{ fill: "#00A651" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 20 }}>
          <h3 style={{ color: "#fff", fontSize: 15, fontWeight: "600", margin: "0 0 16px 0" }}>Revenue This Week (Le)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="name" stroke="#666" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis stroke="#666" tick={{ fill: "#666", fontSize: 12 }} />
              <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #333", color: "#fff" }} />
              <Bar dataKey="revenue" fill="#00A651" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 20 }}>
        <h3 style={{ color: "#fff", fontSize: 15, fontWeight: "600", margin: "0 0 16px 0" }}>Recent Orders</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["Order ID", "Amount", "Payment", "Status", "Date"].map(h => <th key={h} style={{ color: "#666", fontSize: 12, fontWeight: "600", textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #222", textTransform: "uppercase" }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {recentOrders.map(order => (
              <tr key={order.id} style={{ borderBottom: "1px solid #1a1a1a" }}>
                <td style={{ color: "#00A651", fontSize: 14, padding: "12px", fontFamily: "monospace", fontWeight: "600" }}>#{order.id.slice(0, 8).toUpperCase()}</td>
                <td style={{ color: "#ccc", fontSize: 14, padding: "12px" }}>Le {order.total_amount?.toLocaleString()}</td>
                <td style={{ color: "#ccc", fontSize: 14, padding: "12px", textTransform: "capitalize" }}>{order.payment_method?.replace("_", " ")}</td>
                <td style={{ color: "#ccc", fontSize: 14, padding: "12px" }}>
                  <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: "600", background: (statusColor[order.status] || "#999") + "20", color: statusColor[order.status] || "#999" }}>{order.status}</span>
                </td>
                <td style={{ color: "#ccc", fontSize: 14, padding: "12px" }}>{new Date(order.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
